/**
 * hatchBoundaryGeometry — the single canonical HATCH boundary walk + the exact
 * arc / ellipse → cubic-Bézier conversion shared by BOTH render pipes.
 *
 * AutoCAD renders arcs as TRUE curves; the PDF `c` operator is a cubic Bézier,
 * so we emit mathematically exact cubics (no facet error) by splitting any arc
 * into spans of ≤90° and using the standard tangent-length formula
 * `k = 4/3 · tan(Δθ/4)`. The scene tessellator samples the SAME parametric
 * curve at a fixed segment count instead.
 *
 * Historically this math lived in `apps/web/.../converters/arcBezier.ts` (PDF)
 * AND was re-written in `scene3d/adapters/hatchToRenderable.ts` (scene), and the
 * two copies drifted (subtly different CCW sweep normalisation, the scene copy
 * lost the `sweep===0 → ±2π` full-circle guard). This module is the ONE source
 * of truth: a single {@link normalizeSweep} (the PDF version, full-circle guard
 * included) and a neutral parametric IR ({@link edgeToCurve} → a curve with
 * `sample(t)` / `toBeziers()` / `toPolyline(n)`) so PDF and scene cannot diverge
 * again.
 *
 * Pure CAD math owned by `drawing-model` (precedent: `splineMath.ts`). The only
 * import is the TYPE-ONLY model boundary edge/path shapes — NO three / NO earcut
 * / NO pdf-lib / NO apps/web. Both render pipes project this neutral IR into
 * their own loop type via {@link walkHatchBoundary}'s generic emitter.
 */

import type { HatchBoundaryEdge, HatchBoundaryPath } from "./model";
import {
  distanceToClosedPolyline,
  isPointInFilledRegion,
  renderedBoundaryLoopIndices,
} from "./pointGeometry";

/* ── Neutral geometric IR (no apps/web cadPrimitive dependency) ─────────── */

/**
 * 2D point. Field-identical to the PDF exporter's `cadPrimitive.Point2`, so a
 * {@link BezierLoop} assigns structurally to a `PathSubpath` with no copy.
 */
export interface Point2 {
  x: number;
  y: number;
}

/**
 * A cubic Bézier segment with two control points and an end point. Arcs /
 * circles / ellipses are split into ≤90° spans and emitted as exact cubics.
 */
export interface BezierSegment {
  kind: "bezier";
  c1: Point2;
  c2: Point2;
  to: Point2;
}

/** A straight segment to `to`. */
export interface LineSegment {
  kind: "line";
  to: Point2;
}

/** One drawable segment of a loop. The loop's start point is `BezierLoop.start`. */
export type EdgeSegment = LineSegment | BezierSegment;

/** A closed loop: a seed `start` point plus the chain of segments after it. */
export interface BezierLoop {
  start: Point2;
  segments: EdgeSegment[];
}

/** Axis-aligned bounding box (world mm). Field-identical to PDF's `LoopBbox`. */
export interface Bbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** DXF angles (HATCH boundary arc/ellipse start/end) are stored in DEGREES. */
const DEG_TO_RAD = Math.PI / 180;

/** Max span angle per bezier segment (radians) — 90°. */
const MAX_SPAN = Math.PI / 2;

/* ── Canonical CCW sweep + exact bezier conversion (verbatim from arcBezier) ─ */

/**
 * Normalise an arc sweep from start→end given the CCW flag, matching
 * `tessellateArc`'s convention. This is THE canonical version (full-circle
 * guard included) — PDF and scene both read it so they never drift again.
 */
export function normalizeSweep(
  startAngle: number,
  endAngle: number,
  ccw: boolean,
): number {
  let sweep = endAngle - startAngle;
  if (ccw) {
    if (sweep <= 0) sweep += Math.PI * 2;
  } else {
    if (sweep >= 0) sweep -= Math.PI * 2;
  }
  // Guard against a 0 sweep that should be a full circle.
  if (sweep === 0) sweep = ccw ? Math.PI * 2 : -Math.PI * 2;
  return sweep;
}

/**
 * Bezier control-tangent length factor for a span of `delta` radians.
 * `k = 4/3 · tan(delta/4)`. Sign follows `delta`.
 */
function tangentFactor(delta: number): number {
  return (4 / 3) * Math.tan(delta / 4);
}

/**
 * Sample an arc as a list of bezier segments. The caller supplies an
 * `evalPoint(angle)` mapping a parameter angle to a RAW world point and an
 * `evalDeriv(angle)` mapping it to the unit-less derivative direction
 * (dx/dθ, dy/dθ) at that angle. This lets one routine serve both circular
 * arcs (radius·{−sin,cos}) and ellipse arcs (axis-rotated derivative).
 *
 * Returns the segments **after** the start point; the caller seeds `start`
 * with `evalPoint(startAngle)`.
 */
export function arcSpansToBeziers(
  startAngle: number,
  sweep: number,
  evalPoint: (angle: number) => Point2,
  evalDeriv: (angle: number) => Point2,
): { start: Point2; segments: BezierSegment[] } {
  const start = evalPoint(startAngle);
  const segments: BezierSegment[] = [];
  const total = Math.abs(sweep);
  const spanCount = Math.max(1, Math.ceil(total / MAX_SPAN));
  const dir = Math.sign(sweep) || 1;
  const span = (total / spanCount) * dir;

  let a0 = startAngle;
  let p0 = start;
  let d0 = evalDeriv(a0);
  const k = tangentFactor(span);
  for (let i = 0; i < spanCount; i++) {
    const a1 = a0 + span;
    const p1 = evalPoint(a1);
    const d1 = evalDeriv(a1);
    // Control points: P0 + k·D0 and P1 − k·D1, where D is the derivative
    // dP/dθ (so the chord-tangent scaling lands on a unit circle correctly).
    const c1: Point2 = { x: p0.x + k * d0.x, y: p0.y + k * d0.y };
    const c2: Point2 = { x: p1.x - k * d1.x, y: p1.y - k * d1.y };
    segments.push({ kind: "bezier", c1, c2, to: p1 });
    a0 = a1;
    p0 = p1;
    d0 = d1;
  }
  return { start, segments };
}

/**
 * Circular arc (center + radius, start/end angle in radians). `ccw=true`
 * matches a DXF ARC (always CCW from startAngle to endAngle). For a full
 * CIRCLE pass startAngle=0, endAngle=2π.
 */
export function circularArcToBeziers(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
): { start: Point2; segments: BezierSegment[] } {
  const sweep = normalizeSweep(startAngle, endAngle, ccw);
  const evalPoint = (a: number): Point2 => ({
    x: centerX + radius * Math.cos(a),
    y: centerY + radius * Math.sin(a),
  });
  // dP/dθ = radius·(−sin, cos)
  const evalDeriv = (a: number): Point2 => ({
    x: -radius * Math.sin(a),
    y: radius * Math.cos(a),
  });
  return arcSpansToBeziers(startAngle, sweep, evalPoint, evalDeriv);
}

/**
 * Full circle as a closed bezier loop (4 quarter-arc cubics).
 */
export function circleToBeziers(
  centerX: number,
  centerY: number,
  radius: number,
): { start: Point2; segments: BezierSegment[] } {
  return circularArcToBeziers(centerX, centerY, radius, 0, Math.PI * 2, true);
}

/**
 * Ellipse / ellipse-arc → beziers. `center` is the ellipse centre, `majorX`/
 * `majorY` is the major-axis endpoint vector (relative to centre), `ratio` is
 * minor/major, and `startParam`/`endParam` are the DXF parameters (radians,
 * CCW from the major axis). A full ellipse is startParam=0, endParam=2π.
 *
 * The parametric point at angle t is:
 *   local = (major·cos t, minor·sin t)   (in axis-aligned space)
 *   world = center + R(φ)·local           (φ = atan2(majorY, majorX))
 * and the derivative dP/dt = R(φ)·(−major·sin t, minor·cos t).
 *
 * `counterclockwise` (default true) selects the sweep DIRECTION from start→end,
 * mirroring {@link circularArcToBeziers}/`normalizeSweep`. An ELLIPSE entity
 * always traces CCW so its caller omits the flag; a HATCH `ellipticArc` boundary
 * edge carries `73=0` (clockwise) and MUST trace the OTHER way, otherwise the
 * clip fills the opposite side of the ellipse.
 */
export function ellipseToBeziers(
  centerX: number,
  centerY: number,
  majorX: number,
  majorY: number,
  ratio: number,
  startParam: number,
  endParam: number,
  counterclockwise = true,
): { start: Point2; segments: BezierSegment[] } {
  const majorMag = Math.hypot(majorX, majorY);
  if (majorMag === 0) {
    const p: Point2 = { x: centerX, y: centerY };
    return { start: p, segments: [] };
  }
  const minorMag = majorMag * ratio;
  const cosF = majorX / majorMag;
  const sinF = majorY / majorMag;

  // ELLIPSE parameter space is CCW; the sweep sign follows `counterclockwise`,
  // matching normalizeSweep's convention (positive sweep = CCW, negative = CW)
  // so a clockwise (73=0) boundary edge traces start→end the OTHER way around.
  let sweep = endParam - startParam;
  if (counterclockwise) {
    if (sweep <= 0) sweep += Math.PI * 2;
  } else {
    if (sweep >= 0) sweep -= Math.PI * 2;
  }
  // A full revolution stays a full revolution (sign carries the direction).
  if (Math.abs(endParam - startParam) >= Math.PI * 2 - 1e-9) {
    sweep = counterclockwise ? Math.PI * 2 : -Math.PI * 2;
  }

  const evalPoint = (t: number): Point2 => {
    const lx = majorMag * Math.cos(t);
    const ly = minorMag * Math.sin(t);
    return {
      x: centerX + lx * cosF - ly * sinF,
      y: centerY + lx * sinF + ly * cosF,
    };
  };
  const evalDeriv = (t: number): Point2 => {
    const dlx = -majorMag * Math.sin(t);
    const dly = minorMag * Math.cos(t);
    return {
      x: dlx * cosF - dly * sinF,
      y: dlx * sinF + dly * cosF,
    };
  };
  return arcSpansToBeziers(startParam, sweep, evalPoint, evalDeriv);
}

/* ── Neutral parametric IR of one boundary edge ─────────────────────────── */

/** Common surface every edge curve presents to both render pipes. */
interface EdgeCurveBase {
  /** Discriminant. */
  readonly kind: "line" | "arc" | "ellipticArc";
  /** Point on the curve at t∈[0,1] (t=0 → start, t=1 → end). */
  sample(t: number): Point2;
  /** Exact cubics ≤90° (PDF): the start seed + chained bezier/line segments. */
  toBeziers(): { start: Point2; segments: EdgeSegment[] };
  /** `segmentCount + 1` evenly-parameterised samples (scene polyline). */
  toPolyline(segmentCount: number): Point2[];
}

/** A straight boundary edge (line). */
export interface LineEdgeCurve extends EdgeCurveBase {
  readonly kind: "line";
}

/** A circular-arc boundary edge. */
export interface ArcEdgeCurve extends EdgeCurveBase {
  readonly kind: "arc";
}

/** An elliptic-arc boundary edge. */
export interface EllipticArcEdgeCurve extends EdgeCurveBase {
  readonly kind: "ellipticArc";
}

/** The neutral parametric IR of one boundary edge. */
export type HatchEdgeCurve = LineEdgeCurve | ArcEdgeCurve | EllipticArcEdgeCurve;

/** Sample `segmentCount + 1` evenly-parameterised points along a curve. */
function samplePolyline(
  curve: { sample(t: number): Point2 },
  segmentCount: number,
): Point2[] {
  const n = Math.max(1, Math.floor(segmentCount));
  const out: Point2[] = [];
  for (let i = 0; i <= n; i++) {
    out.push(curve.sample(i / n));
  }
  return out;
}

/**
 * Convert ONE boundary edge to its neutral parametric curve. DXF stores the
 * arc / ellipse start+end angles in DEGREES; the radians conversion happens
 * HERE in exactly ONE place (`degToRad=true`, the default) — both render pipes
 * pass the raw model edge and never multiply by `DEG_TO_RAD` themselves, which
 * is what historically caused the pre-FIX-4 double-unit bug.
 *
 * Returns `null` for a degenerate edge (the walk skips it, matching the PDF
 * `continue`):
 *   - arc with `radius <= 0`,
 *   - ellipticArc with `minorRatio <= 0` or a zero-magnitude major axis.
 */
export function edgeToCurve(
  edge: HatchBoundaryEdge,
  degToRad = true,
): HatchEdgeCurve | null {
  const angScale = degToRad ? DEG_TO_RAD : 1;

  if (edge.edgeType === "line") {
    const start: Point2 = { x: edge.start.x, y: edge.start.y };
    const end: Point2 = { x: edge.end.x, y: edge.end.y };
    const sample = (t: number): Point2 => ({
      x: start.x + (end.x - start.x) * t,
      y: start.y + (end.y - start.y) * t,
    });
    const curve: LineEdgeCurve = {
      kind: "line",
      sample,
      toBeziers: () => ({ start, segments: [{ kind: "line", to: end }] }),
      toPolyline: (segmentCount: number) => samplePolyline({ sample }, segmentCount),
    };
    return curve;
  }

  if (edge.edgeType === "arc") {
    if (edge.radius <= 0) return null;
    const cx = edge.center.x;
    const cy = edge.center.y;
    const r = edge.radius;
    const sa = edge.startAngle * angScale;
    const ea = edge.endAngle * angScale;
    const ccw = edge.counterclockwise;
    const sweep = normalizeSweep(sa, ea, ccw);
    const sample = (t: number): Point2 => {
      const a = sa + sweep * t;
      return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };
    const curve: ArcEdgeCurve = {
      kind: "arc",
      sample,
      toBeziers: () => circularArcToBeziers(cx, cy, r, sa, ea, ccw),
      toPolyline: (segmentCount: number) => samplePolyline({ sample }, segmentCount),
    };
    return curve;
  }

  // ellipticArc
  if (edge.minorRatio <= 0) return null;
  const cx = edge.center.x;
  const cy = edge.center.y;
  const majX = edge.majorAxisEnd.x;
  const majY = edge.majorAxisEnd.y;
  const majorMag = Math.hypot(majX, majY);
  if (majorMag === 0) return null;
  const minorMag = majorMag * edge.minorRatio;
  const cosF = majX / majorMag;
  const sinF = majY / majorMag;
  const ccw = edge.counterclockwise;
  const sp = edge.startAngle * angScale;
  const ep = edge.endAngle * angScale;
  // Same sweep normalisation `ellipseToBeziers` uses (full-revolution guard).
  let sweep = ep - sp;
  if (ccw) {
    if (sweep <= 0) sweep += Math.PI * 2;
  } else {
    if (sweep >= 0) sweep -= Math.PI * 2;
  }
  if (Math.abs(ep - sp) >= Math.PI * 2 - 1e-9) {
    sweep = ccw ? Math.PI * 2 : -Math.PI * 2;
  }
  const sample = (t: number): Point2 => {
    const a = sp + sweep * t;
    const lx = majorMag * Math.cos(a);
    const ly = minorMag * Math.sin(a);
    return {
      x: cx + lx * cosF - ly * sinF,
      y: cy + lx * sinF + ly * cosF,
    };
  };
  const curve: EllipticArcEdgeCurve = {
    kind: "ellipticArc",
    sample,
    toBeziers: () =>
      ellipseToBeziers(cx, cy, majX, majY, edge.minorRatio, sp, ep, ccw),
    toPolyline: (segmentCount: number) => samplePolyline({ sample }, segmentCount),
  };
  return curve;
}

/* ── Shared boundary walk ───────────────────────────────────────────────── */

/**
 * Per-consumer projection of a boundary walk into a loop type `L` (BezierLoop
 * for PDF, a polyline loop for the scene). The walk drives the iteration; the
 * emitter owns the per-edge join policy and the loop accept/reject threshold.
 *
 *   - `beginLoop(path)` — reset the running loop accumulator.
 *   - `emitEdge(curve, isFirstEdge)` — append one edge's geometry; the emitter
 *     decides how to JOIN a non-first edge whose start may not coincide with the
 *     previous edge's end (PDF inserts a straight join; the scene dedups).
 *   - `endLoop(path)` — finalise the accumulator into an `L`, or `null` to drop
 *     a degenerate loop (no usable edges / too few vertices).
 */
export interface HatchLoopEmitter<L> {
  beginLoop(path: HatchBoundaryPath): void;
  emitEdge(curve: HatchEdgeCurve, isFirstEdge: boolean): void;
  endLoop(path: HatchBoundaryPath): L | null;
}

/**
 * Walk every boundary path → for each path, convert its edges to neutral curves
 * (skipping degenerate ones) and feed them to the emitter, then collect the
 * non-null finalised loops. This is the ONE boundary traversal both pipes share;
 * the geometry math + degenerate skip live here, the loop shape + join policy in
 * the emitter.
 */
export function walkHatchBoundary<L>(
  paths: readonly HatchBoundaryPath[],
  emitter: HatchLoopEmitter<L>,
): L[] {
  const loops: L[] = [];
  for (const path of paths) {
    emitter.beginLoop(path);
    let isFirstEdge = true;
    for (const edge of path.edges) {
      const curve = edgeToCurve(edge);
      if (curve === null) continue; // degenerate edge → skip (matches PDF `continue`)
      emitter.emitEdge(curve, isFirstEdge);
      isFirstEdge = false;
    }
    const loop = emitter.endLoop(path);
    if (loop !== null) loops.push(loop);
  }
  return loops;
}

/* ── Bbox over bezier loops ─────────────────────────────────────────────── */

/**
 * Bounding box over every bezier loop, INCLUDING control points (the bezier
 * hull) — matches the PDF `loopsBbox` so the pattern-line extent is identical.
 * Returns `null` when no finite point was accumulated.
 */
export function bezierLoopsBbox(loops: readonly BezierLoop[]): Bbox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const acc = (p: Point2): void => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };
  for (const loop of loops) {
    acc(loop.start);
    for (const seg of loop.segments) {
      if (seg.kind === "bezier") {
        acc(seg.c1);
        acc(seg.c2);
      }
      acc(seg.to);
    }
  }
  if (!Number.isFinite(minX) || maxX < minX || maxY < minY) return null;
  return { minX, minY, maxX, maxY };
}

/* ── HATCH pick hit-test (HATCHEDIT select) ─────────────────────────────────
 *
 * The HATCHEDIT click-select must hit-test a clicked point against a placed
 * hatch's REAL boundary geometry (HATCH `EntityPreview` previews carry NO
 * Center/Insertion coords, so the generic preview-string `findClosestEntity`
 * always misses — every hatch is skipped and the click is a dead no-op).
 *
 * This is the SAME hatch hit-test the main canvas selection performs
 * (`scene3d/raycast/distance.ts` `distanceToRenderable` `case "hatch"`):
 * point-in-polygon on the outermost (exterior) boundary loop → distance 0
 * (inside), else the nearest distance to the loop polyline. The boundary walk
 * is the ONE shared `walkHatchBoundary` truth (no per-pipe re-tessellation),
 * and the point-in-polygon is the standard ray-cast. Living here (a
 * `drawing-model` leaf) lets the scene draft state machine reuse it WITHOUT a
 * `scene → scene3d` dependency edge.
 */

/** Minimal typed-hatch shape this hit-test needs (a structural subset of the
 *  model `HatchEntity` — id + boundary paths). */
export interface HatchPickCandidate {
  readonly id: string;
  readonly boundaryPaths: readonly HatchBoundaryPath[];
}

/** Segment count used to flatten arc / elliptic-arc boundary edges for the
 *  pick polygon. Matches the scene hatch tessellation (32) so the pick polygon
 *  is vertex-identical to what the canvas hit-test uses. */
const HATCH_PICK_ARC_SEGMENTS = 32;

/** Polygon-loop emitter: flattens each boundary path to a simple `Point2[]`
 *  ring with the SAME join/dedup/close policy the scene render emitter uses
 *  (drop a coincident first vertex, pop the closing duplicate, drop `<3`). */
function createPickPolygonEmitter(): HatchLoopEmitter<{
  kind: "exterior" | "interior";
  vertices: Point2[];
}> {
  let vertices: Point2[] = [];
  let lastVertex: Point2 | null = null;
  const samePoint = (a: Point2, b: Point2): boolean =>
    Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;
  return {
    beginLoop() {
      vertices = [];
      lastVertex = null;
    },
    emitEdge(curve) {
      const segmentCount =
        curve.kind === "line" ? 1 : HATCH_PICK_ARC_SEGMENTS;
      const samples = curve.toPolyline(segmentCount);
      if (samples.length === 0) return;
      let startIdx = 0;
      const first = samples[0];
      if (lastVertex !== null && first !== undefined && samePoint(lastVertex, first)) {
        startIdx = 1;
      }
      for (let i = startIdx; i < samples.length; i++) {
        const v = samples[i];
        if (v !== undefined) vertices.push({ x: v.x, y: v.y });
      }
      lastVertex = samples[samples.length - 1] ?? lastVertex;
    },
    endLoop(path) {
      if (vertices.length >= 2) {
        const f = vertices[0];
        const l = vertices[vertices.length - 1];
        if (f && l && samePoint(f, l)) vertices.pop();
      }
      if (vertices.length < 3) return null;
      const kind: "exterior" | "interior" =
        (path.pathTypeFlag ?? 0) & 1 ? "exterior" : "interior";
      return { kind, vertices: vertices.slice() };
    },
  };
}

/**
 * Hit-test distance from `point` to a single hatch's boundary geometry.
 * 0 when the point is inside the FILLED region (inside an exterior loop AND NOT
 * inside one of that island's holes), else the nearest distance to the closest
 * usable loop polyline. Returns `Infinity` when the hatch has no usable boundary
 * (degenerate) — that candidate is then skipped. This is the same per-hatch
 * distance the canvas `distanceToRenderable` `case "hatch"` computes; both share
 * the one-truth `isPointInFilledRegion` (inside ⇔ the renderer fills the point).
 *
 * Codex round-4 #4 — a hatch can carry MULTIPLE exterior boundaryPaths (e.g.
 * BHATCH over several disjoint islands, or a non-contiguous region). The pick
 * must be inside the hatch when the click lands inside ANY exterior loop, not
 * just the first one the walk emits. The shared predicate tests every exterior
 * AND subtracts each island's holes, so a click in an EMPTY hole of a later
 * island does NOT select the hatch (it falls through to the nearest-edge
 * distance). When outside the filled region, return the nearest distance across
 * all usable loops (the closest polyline, exterior or not).
 */
export function distanceToHatchBoundary(
  point: Point2,
  hatch: HatchPickCandidate,
): number {
  const loops = walkHatchBoundary(hatch.boundaryPaths, createPickPolygonEmitter());
  if (loops.length === 0) return Infinity;
  // Usable loops only (a tessellated ring needs >=3 vertices to bound an area).
  const usable = loops.filter((l) => l.vertices.length >= 3);
  if (usable.length === 0) return Infinity;
  // Did the RAW boundary tag any EXTERIOR path (`pathTypeFlag` bit 0)? The walk's
  // emitter DROPS a degenerate (<3-vertex) loop BEFORE assigning its kind, so a
  // tagged-but-degenerate exterior would otherwise be invisible to the fill
  // predicate. Reading the raw flag preserves the tagged-exterior signal: when an
  // exterior WAS tagged but no usable exterior survived, the renderer fills
  // NOTHING — so the pick must NOT promote an interior loop to fill either (PR
  // #410 P3). We inject a synthetic degenerate exterior so the shared predicate's
  // `hadTaggedExterior` gate suppresses the no-exterior fallback identically.
  const rawHadExterior = hatch.boundaryPaths.some(
    (p) => ((p.pathTypeFlag ?? 0) & 1) !== 0,
  );
  const usableHasExterior = usable.some((l) => l.kind === "exterior");
  const predicateLoops: { vertices: Point2[]; isHole: boolean }[] = usable.map(
    (l) => ({ vertices: l.vertices, isHole: l.kind !== "exterior" }),
  );
  if (rawHadExterior && !usableHasExterior) {
    // A tagged exterior degenerated. Prepend a degenerate exterior marker (0
    // verts → filtered from the predicate's usable set) so `hadTaggedExterior` is
    // true and the fallback is suppressed → reads EMPTY, never interior-as-fill.
    predicateLoops.unshift({ vertices: [], isHole: false });
  }
  // Inside the FILLED region (exterior minus that island's holes; with the
  // no-exterior→first-loop fallback, suppressed when a tagged exterior
  // degenerated) ⇒ inside the hatch (distance 0). Shared one-truth so the pick
  // agrees with the render cell-for-cell.
  if (isPointInFilledRegion(point, predicateLoops)) {
    return 0;
  }
  // Outside the filled region (incl. inside an empty hole) → nearest distance,
  // measured ONLY over the loops the renderer actually emits (every exterior +
  // each OWNED hole). An UNOWNED orphan hole is DROPPED by `tessellateHatchLoops`
  // and renders nothing, so a click near it must NOT register as near the hatch
  // (PR #410 P3). `renderedBoundaryLoopIndices` is the SHARED one-truth for
  // "which loops the renderer draws" (same fn the canvas pick uses), so the
  // measured loop set equals the rendered fill edges cell-for-cell.
  //
  // `rawHadExterior` threads the tagged-degenerate signal: when an exterior WAS
  // tagged but degenerated away (no usable exterior), the renderer fills NOTHING,
  // so the rendered-loop set is EMPTY and the nearest distance stays Infinity —
  // a click near the leftover interior loop must NOT pick (PR #410 P3), mirroring
  // the empty-fill predicate above.
  const rendered = renderedBoundaryLoopIndices(
    usable.map((l) => ({ vertices: l.vertices, isHole: l.kind !== "exterior" })),
    rawHadExterior,
  );
  let nearest = Infinity;
  for (const i of rendered) {
    const d = distanceToClosedPolyline(point, usable[i]!.vertices);
    if (d < nearest) nearest = d;
  }
  return nearest;
}

/**
 * Pick the hatch whose boundary geometry is closest to `point` (0 = the point
 * is inside it), subject to a pick `tolerance` cutoff. Returns the picked
 * candidate's `id`, or `null` when no hatch is WITHIN tolerance (or none has
 * usable boundary geometry). Candidates with degenerate boundaries are skipped.
 *
 * ## Pick tolerance (Codex P2-a — empty-canvas click must NOT pick)
 *
 * The canvas selection pick (`scene3d/raycast/pick.ts:pickFromCandidates`)
 * rejects any candidate whose stroke-distance exceeds `toleranceWorld` (a
 * `PICK_APERTURE_PX = 4` CSS-px aperture re-expressed in world units). Without a
 * matching cutoff here a click ANYWHERE on the empty canvas accepted the nearest
 * hatch — HATCHEDIT then advanced to "editing" an entity the user never aimed at.
 *
 * A hit qualifies when its distance is `<= tolerance`. `tolerance` defaults to
 * `0`, so an inside-hit (distance `0`) always qualifies but a click OUTSIDE every
 * boundary returns `null` unless the caller threads the canvas pick aperture
 * (the world-per-pixel scale lives at the call site, not here). The select
 * handler in `draftModifyStateMachine` passes the same world-space tolerance the
 * canvas pick uses so the aperture feels identical to the user; callers without
 * a scale (and the unit tests) rely on the inside-only default.
 *
 * Tie-break (two overlapping hatches, the click is INSIDE both ⇒ distance 0
 * for each): the LATER candidate in iteration order wins. The caller passes
 * `hatches` in document/entity order, where a later entity has a higher
 * draw-order `zOrder` and is therefore TOPMOST — matching the canvas pick's
 * `compareTopmostFirst` convention (zOrder DESC). Using `<=` (not `<`) lets a
 * later equal-distance hit overwrite an earlier one, so HATCHEDIT edits the
 * visible top hatch rather than a hidden bottom one.
 */
export function findClosestHatchPick(
  point: Point2,
  hatches: readonly HatchPickCandidate[],
  tolerance = 0,
): string | null {
  // Normalize a NaN / negative tolerance to the inside-only default so a bad
  // caller value can never widen the aperture to "accept anything".
  const cutoff = Number.isFinite(tolerance) && tolerance > 0 ? tolerance : 0;
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const hatch of hatches) {
    const d = distanceToHatchBoundary(point, hatch);
    // Skip degenerate candidates (Infinity) so an all-degenerate list still
    // yields null — `Infinity <= Infinity` would otherwise latch onto a hatch
    // with no usable geometry.
    if (!Number.isFinite(d)) continue;
    // Pick-aperture cutoff: a candidate beyond the tolerance is NOT a hit
    // (inside-hits have distance 0 and always pass with the default cutoff=0).
    if (d > cutoff) continue;
    // `<=` so that, on an equal-distance tie, the LATER (topmost draw-order)
    // candidate replaces the earlier one — the topmost-wins pick convention.
    if (d <= bestDist) {
      bestDist = d;
      bestId = hatch.id;
    }
  }
  return bestId;
}
